import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { message } from "antd";
import Utilities from "../../dependencies/custom/react-utilities";
import domainSettings from "../../dependencies/custom/settings";

export const useHolidayPackages = () => {
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchPackages = useCallback(
    async (params = {}) => {
      setLoading(true);
      try {
        const queryObj = {};
        if (params.acadyrID) queryObj.acadyrID = params.acadyrID;
        if (params.termID) queryObj.termID = params.termID;
        if (params.school_class) queryObj.school_class = params.school_class;
        if (params.subjectID) queryObj.subjectID = params.subjectID;
        if (params.include_inactive) queryObj.include_inactive = "true";

        const queryParams = new URLSearchParams(queryObj).toString();
        const url = `${domainSettings.urls.academics.holidayPackages}${queryParams ? "?" + queryParams : ""}`;
        const response = await Utilities.fetchWithAuth(
          "GET",
          url,
          null,
          navigate,
        );

        if (response.ok) {
          setPackages(
            Array.isArray(response.data)
              ? response.data
              : response.data?.results || [],
          );
        } else {
          Utilities.showError(response, "Failed to fetch holiday packages");
        }
      } catch (error) {
        message.error("Error fetching holiday packages");
        console.error(error);
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  const fetchPackageDetail = useCallback(
    async (id) => {
      try {
        const url = `${domainSettings.urls.academics.holidayPackageDetail.replace(":id", id)}?include_inactive=true`;
        const response = await Utilities.fetchWithAuth(
          "GET",
          url,
          null,
          navigate,
        );
        if (response.ok) return response.data;
        Utilities.showError(response, "Failed to fetch package details");
        return null;
      } catch (error) {
        message.error("Error fetching package details");
        console.error(error);
        return null;
      }
    },
    [navigate],
  );

  const createPackage = useCallback(
    async (formData) => {
      setLoading(true);
      try {
        const response = await Utilities.fetchWithAuth(
          "POST",
          domainSettings.urls.academics.holidayPackages,
          formData,
          navigate,
        );
        if (response.ok) {
          message.success("Holiday package created successfully");
          return response.data;
        }
        Utilities.showError(response, "Failed to create holiday package");
        return null;
      } catch (error) {
        message.error("Error creating holiday package");
        console.error(error);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  const updatePackage = useCallback(
    async (id, data) => {
      setLoading(true);
      try {
        const url = domainSettings.urls.academics.holidayPackageDetail.replace(
          ":id",
          id,
        );
        const response = await Utilities.fetchWithAuth(
          "PATCH",
          url,
          data,
          navigate,
        );
        if (response.ok) {
          message.success("Holiday package updated successfully");
          return response.data;
        }
        Utilities.showError(response, "Failed to update holiday package");
        return null;
      } catch (error) {
        message.error("Error updating holiday package");
        console.error(error);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  const activatePackage = useCallback(
    async (id) => {
      try {
        const url =
          domainSettings.urls.academics.holidayPackageActivate.replace(
            ":id",
            id,
          );
        const response = await Utilities.fetchWithAuth(
          "POST",
          url,
          null,
          navigate,
        );
        if (response.ok) {
          message.success(
            response.data?.detail || "Holiday package activated successfully",
          );
          return true;
        }
        Utilities.showError(response, "Failed to activate holiday package");
        return false;
      } catch (error) {
        message.error("Error activating holiday package");
        console.error(error);
        return false;
      }
    },
    [navigate],
  );

  const deactivatePackage = useCallback(
    async (id) => {
      try {
        const url =
          domainSettings.urls.academics.holidayPackageDeactivate.replace(
            ":id",
            id,
          );
        const response = await Utilities.fetchWithAuth(
          "POST",
          url,
          null,
          navigate,
        );
        if (response.ok) {
          message.success(
            response.data?.detail || "Holiday package deactivated successfully",
          );
          return true;
        }
        Utilities.showError(response, "Failed to deactivate holiday package");
        return false;
      } catch (error) {
        message.error("Error deactivating holiday package");
        console.error(error);
        return false;
      }
    },
    [navigate],
  );

  const archivePackage = useCallback(
    async (id) => {
      try {
        const url = domainSettings.urls.academics.holidayPackageArchive.replace(
          ":id",
          id,
        );
        const response = await Utilities.fetchWithAuth(
          "POST",
          url,
          null,
          navigate,
        );
        if (response.ok) {
          message.success(
            response.data?.detail || "Holiday package archived successfully",
          );
          return true;
        }
        Utilities.showError(response, "Failed to archive holiday package");
        return false;
      } catch (error) {
        message.error("Error archiving holiday package");
        console.error(error);
        return false;
      }
    },
    [navigate],
  );

  const hardDeletePackage = useCallback(
    async (id) => {
      try {
        const url = domainSettings.urls.academics.holidayPackageDetail.replace(
          ":id",
          id,
        );
        const response = await Utilities.fetchWithAuth(
          "DELETE",
          url,
          null,
          navigate,
        );
        if (response.ok) {
          message.success("Holiday package deleted successfully");
          return true;
        }
        Utilities.showError(response, "Failed to delete holiday package");
        return false;
      } catch (error) {
        message.error("Error deleting holiday package");
        console.error(error);
        return false;
      }
    },
    [navigate],
  );

  const uploadPackageFiles = useCallback(
    async (id, files) => {
      try {
        const formData = new FormData();
        files.forEach((f) => formData.append("files", f));
        const url = domainSettings.urls.academics.holidayPackageFiles.replace(
          ":id",
          id,
        );
        const response = await Utilities.fetchWithAuth(
          "POST",
          url,
          formData,
          navigate,
        );
        if (response.ok) {
          message.success("Files uploaded successfully");
          return response.data;
        }
        Utilities.showError(response, "Failed to upload files");
        return null;
      } catch (error) {
        message.error("Error uploading files");
        console.error(error);
        return null;
      }
    },
    [navigate],
  );

  const deletePackageFile = useCallback(
    async (packageId, fileId) => {
      try {
        const url = domainSettings.urls.academics.holidayPackageFileDelete
          .replace(":id", packageId)
          .replace(":fileId", fileId);
        const response = await Utilities.fetchWithAuth(
          "DELETE",
          url,
          null,
          navigate,
        );
        if (response.ok) {
          message.success("File removed successfully");
          return true;
        }
        Utilities.showError(response, "Failed to remove file");
        return false;
      } catch (error) {
        message.error("Error removing file");
        console.error(error);
        return false;
      }
    },
    [navigate],
  );

  return {
    packages,
    loading,
    fetchPackages,
    fetchPackageDetail,
    createPackage,
    updatePackage,
    activatePackage,
    deactivatePackage,
    archivePackage,
    hardDeletePackage,
    uploadPackageFiles,
    deletePackageFile,
  };
};

export default useHolidayPackages;
